import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { db } from '../serices/firebase';
import { collection, query, orderBy, onSnapshot } from 'firebase/firestore';
import * as Icons from 'lucide-react'; 
import { ArrowRight, Loader2, Trophy, Search, X, Layers } from 'lucide-react';

const Categories = () => {
  const [groups, setGroups] = useState([]);
  const [awards, setAwards] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const unsubMain = onSnapshot(query(collection(db, "main_categories"), orderBy("name", "asc")), (snap) => {
      setGroups(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
    });

    const unsubSub = onSnapshot(query(collection(db, "sub_categories"), orderBy("name", "asc")), (snap) => {
      setAwards(snap.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      setLoading(false);
    });

    return () => { unsubMain(); unsubSub(); };
  }, []);

  const DynamicIcon = ({ name, size = 24, className = "" }) => {
    const IconComponent = Icons[name] || Icons.Award;
    return <IconComponent size={size} className={className} />;
  };

  // --- SEARCH LOGIC ---
  const filteredGroups = groups.filter(group => {
    const groupMatches = group.name.toLowerCase().includes(searchTerm.toLowerCase());
    const awardsInGroupMatch = awards.some(award => 
      award.parentGroupId === group.id && 
      award.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    return groupMatches || awardsInGroupMatch;
  });

  if (loading) return <div className="h-screen bg-black flex items-center justify-center"><Loader2 className="animate-spin text-[#D4AF37]" size={48} /></div>;

  return (
    <div className="min-h-screen bg-black text-white pt-10 pb-20 px-4 md:px-6 relative overflow-hidden font-sans">
      
      {/* Background Decorations */}
      <div className="absolute top-0 right-0 w-[300px] md:w-[600px] h-[300px] md:h-[600px] bg-[#D4AF37]/5 blur-[100px] md:blur-[150px] rounded-full -z-0" />
      
      <div className="max-w-7xl mx-auto relative z-10">
        
        {/* HEADER SECTION */}
        <header className="mb-12 md:mb-20 text-center">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="flex items-center justify-center gap-3 mb-4 text-[#D4AF37]">
            <div className="h-px w-8 md:w-12 bg-[#D4AF37]/30" />
            <span className="text-[9px] md:text-[10px] font-black uppercase tracking-[0.5em] block">Mzinyi Awards 2026</span>
            <div className="h-px w-8 md:w-12 bg-[#D4AF37]/30" />
          </motion.div>
          <h1 className="text-4xl md:text-8xl font-black uppercase italic tracking-tighter leading-none mb-10">
            AWARD <span className="text-[#D4AF37]">CATEGORIES</span>
          </h1>

          {/* --- SEARCH BAR --- */}
          <div className="max-w-2xl mx-auto relative group">
             <div className="absolute inset-y-0 left-5 flex items-center pointer-events-none text-white/20 group-focus-within:text-[#D4AF37] transition-colors">
                <Search size={20} />
             </div>
             <input 
                type="text"
                placeholder="Search Category or Award..."
                className="w-full bg-zinc-900/50 border border-white/10 rounded-2xl md:rounded-full py-5 md:py-6 pl-14 pr-14 outline-none focus:border-[#D4AF37] focus:ring-4 focus:ring-[#D4AF37]/5 text-sm md:text-base font-bold uppercase tracking-widest transition-all placeholder:text-white/10"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
             />
             {searchTerm && (
                <button 
                    onClick={() => setSearchTerm("")}
                    className="absolute inset-y-0 right-5 flex items-center text-white/20 hover:text-white"
                >
                    <X size={20} />
                </button>
             )}
          </div>
        </header>

        {/* --- RESULTS SECTION --- */}
        {filteredGroups.length === 0 ? (
            <div className="py-20 text-center border border-dashed border-white/5 rounded-[2.5rem]">
                <Trophy className="mx-auto text-white/5 mb-6" size={48} />
                <p className="text-white/40 uppercase font-black tracking-widest text-xs">No matching categories found.</p>
            </div>
        ) : (
            filteredGroups.map((group, groupIdx) => (
                <motion.section 
                    key={group.id}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    className="mb-20 md:mb-32"
                >
                    {/* Main Group Header */}
                    <div className="flex items-center gap-4 md:gap-6 mb-8 border-b border-white/5 pb-6">
                        <div className="bg-[#D4AF37] text-black p-3 md:p-4 rounded-xl md:rounded-2xl shadow-lg">
                            <DynamicIcon name={group.icon || 'Layers'} size={24} />
                        </div>
                        <div>
                            <h2 className="text-2xl md:text-5xl font-black uppercase italic tracking-tighter text-white leading-none">
                                {group.name}
                            </h2>
                            <p className="text-[#D4AF37] text-[8px] md:text-[10px] font-bold uppercase tracking-[0.4em] mt-1 opacity-50 italic">
                                Sector Group
                            </p>
                        </div>
                    </div>

                    {/* Sub-categories Grid */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6">
                        {awards
                            .filter(award => award.parentGroupId === group.id)
                            .filter(award => 
                                award.name.toLowerCase().includes(searchTerm.toLowerCase()) || 
                                group.name.toLowerCase().includes(searchTerm.toLowerCase())
                            )
                            .map((award) => (
                                <Link key={award.id} to={`/nominees/${award.name}`}>
                                    <motion.div 
                                        whileHover={{ y: -5, borderColor: "rgba(212, 175, 55, 0.4)" }}
                                        className="bg-zinc-900/30 border border-white/5 p-6 md:p-8 rounded-[1.5rem] md:rounded-[2rem] group transition-all duration-500 hover:bg-[#D4AF37]/5 h-full backdrop-blur-sm relative overflow-hidden flex flex-col justify-between"
                                    >
                                        {/* Decorative Shadow Icon */}
                                        <div className="absolute -right-4 -bottom-4 opacity-[0.02] text-white group-hover:text-[#D4AF37] group-hover:opacity-[0.05] transition-all duration-700">
                                            <DynamicIcon name={award.icon} size={100} />
                                        </div>

                                        <div className="relative z-10">
                                            <div className="text-[#D4AF37] mb-6 opacity-40 group-hover:opacity-100 group-hover:scale-110 transition-all origin-left">
                                                <DynamicIcon name={award.icon} size={28} />
                                            </div>
                                            <h3 className="text-xl md:text-2xl font-black uppercase italic mb-3 tracking-tighter group-hover:text-white transition-colors">
                                                {award.name}
                                            </h3>
                                            <p className="text-white/40 text-[11px] md:text-xs font-light leading-relaxed italic mb-8">
                                                {award.description}
                                            </p>
                                        </div>

                                        <div className="relative z-10 flex items-center justify-between pt-4 border-t border-white/5 group-hover:border-[#D4AF37]/20 transition-all">
                                            <span className="text-[9px] font-black uppercase tracking-widest text-[#D4AF37]">Enter Category</span>
                                            <ArrowRight size={14} className="text-[#D4AF37] transform -translate-x-2 group-hover:translate-x-0 transition-transform" />
                                        </div>
                                    </motion.div>
                                </Link>
                            ))
                        }
                    </div>
                </motion.section>
            ))
        )}
      </div>
    </div>
  );
};

export default Categories;